import { sql } from 'drizzle-orm';
import {
  date,
  pgTable,
  text,
  integer,
  timestamp,
  index,
  foreignKey,
  uuid,
  varchar,
  boolean,
} from 'drizzle-orm/pg-core';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { driver } from '@trashlab/api/src/domains/driver/schemas/driver';
import { defaultFields } from '@trashlab/api/drizzle/default';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas/sku-v2';
import { assets } from '@trashlab/api/src/domains/asset/schemas/assets';

export const recurringWorkOrder = pgTable(
  'recurring_work_order',
  {
    ...defaultFields.fieldsWithTenantId(),
    customerId: uuid('customerId').notNull(),
    customerSiteId: uuid('customerSiteId').notNull(),
    materialId: uuid('materialId').notNull(),
    productId: uuid('productId').notNull(),
    driverId: uuid('driverId'),
    workflowKind: text('workflowKind').notNull(),
    skuId: uuid('skuId'),
    preferredAssetId: uuid('preferredAssetId'),
    letDriverSelectAssetNumber: boolean('letDriverSelectAssetNumber')
      .default(false)
      .notNull(),
    isDriverNoteUrgent: boolean('isDriverNoteUrgent').notNull().default(false),
    driverNotes: text('driverNotes').default(''),
    perWorkOrderPoNumber: varchar('perWorkOrderPoNumber', { length: 255 }),

    // RRULE fields
    frequency: text('frequency').notNull(), // DAILY, WEEKLY, MONTHLY, etc.
    interval: integer('interval').notNull(), // Every X days/weeks/months
    byWeekDay: varchar('byWeekDay').array(), // For weekly recurrence; also used with bySetPos for monthly nth-weekday
    byMonthDay: integer('byMonthDay').array(), // For monthly recurrence by calendar day-of-month
    bySetPos: integer('bySetPos').array(), // For monthly nth-weekday recurrence (1..4 = nth, -1 = last). Combined with byWeekDay.
    startDate: date('startDate').notNull(),
    endDate: date('endDate'), // null = active, set date = "soft deleted" as of that date

    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: text('legacySystemId'),
  },
  (table) => {
    return {
      // Indexes
      site_merge_page_idx: index('recurring_work_order_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      createdAtTs_idx: index('recurring_work_order_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('recurring_work_order_tenantId_idx').on(
        table.tenantId,
      ),
      customerId_idx: index('recurring_work_order_customerId_idx').on(
        table.customerId,
      ),
      customer_merge_page_idx: index(
        'recurring_work_order_customer_merge_page_idx',
      ).on(table.tenantId, table.customerId, table.id),
      customerSiteId_idx: index('recurring_work_order_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      endDate_idx: index('recurring_work_order_endDate_idx').on(table.endDate),
      updatedAtTs_idx: index('recurring_work_order_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('recurring_work_order_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      skuId_idx: index('recurring_work_order_skuId_idx').on(table.skuId),
      preferredAssetId_idx: index('recurring_work_order_preferredAssetId_idx')
        .on(table.preferredAssetId)
        .where(sql`"preferredAssetId" IS NOT NULL`),

      // Foreign keys
      fk_recurring_work_order_customer: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'fk_recurring_work_order_customer',
      }),
      fk_recurring_work_order_customer_site: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'fk_recurring_work_order_customer_site',
      }),
      fk_recurring_work_order_material: foreignKey({
        columns: [table.materialId],
        foreignColumns: [material.id],
        name: 'fk_recurring_work_order_material',
      }),
      fk_recurring_work_order_product: foreignKey({
        columns: [table.productId],
        foreignColumns: [product.id],
        name: 'fk_recurring_work_order_product',
      }),
      fk_recurring_work_order_driver: foreignKey({
        columns: [table.driverId],
        foreignColumns: [driver.id],
        name: 'fk_recurring_work_order_driver',
      }),
      fk_recurring_work_order_sku: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'fk_recurring_work_order_sku',
      }),
      fk_recurring_work_order_preferred_asset: foreignKey({
        columns: [table.preferredAssetId],
        foreignColumns: [assets.id],
        name: 'recurring_work_order_preferredAssetId_fkey',
      }),
    };
  },
);
